import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  
  
  Collapse,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import {
  Announcement,
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  Timeline,
  CheckCircle,
  Warning,
  AccessTime,
} from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminTabs } from '../AdminTabs';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { getAuditLogs } from '../../apis/Admin';
import { useTimezone } from '../../hooks/useTimezone';
import OnboardingTour from '../../components/OnboardingTour';
import Cookies from 'js-cookie';
import { SummaryCard } from '../../components/ui/card';
import { FilterSelect } from '../../components/ui/select';
import { PrimaryButton } from '../../components/ui/button';
import { PageHeader } from '../../components/ui/header';

const themeColor = '#3B9562';
const headerBgColor = '#1B2640';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, type: 'spring', stiffness: 100 } },
};
const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.3, ease: 'easeOut' } }),
};

const thSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  py: 1,
  px: 2,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #E5E7EB',
};
const thSxClickable = {
  ...thSx,
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  '&:hover': { 
    backgroundColor: alpha('#1B2640', 0.85), 
    transition: 'background-color 0.2s ease' 
  },
};
const tdSx = { py: 0.25, px: 2, fontSize: '0.875rem', color: '#374151', verticalAlign: 'middle' };

const Pill = ({ children, color }) => (
  <Box
    sx={{
      display: 'inline-block',
      px: 1.25,
      py: 0.35,
      borderRadius: '10px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      color,
      bgcolor: alpha(color, 0.12),
      textTransform: 'capitalize',
    }}
  >
    {children}
  </Box>
);
Pill.propTypes = { children: PropTypes.node.isRequired, color: PropTypes.string.isRequired };

export default function AdminAuditLog() {
  const { formatDate } = useTimezone();
  const [pageError, setPageError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters / UI state
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('all');
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Sort state
  const [sort, setSort] = useState({ column: 'ts', direction: 'desc' });
  const handleSort = (column) => {
    let direction = 'asc';
    if (sort.column === column && sort.direction === 'asc') direction = 'desc';
    else if (sort.column === column && sort.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    setSort({ column, direction });
  };

  // Raw API logs
  const [apiLogs, setApiLogs] = useState([]);

  // Check if user is superadmin
  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');
  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  // Load logs from API
  useEffect(() => {
  let alive = true;
  setLoading(true);
  setPageError(null);

  const controller = new AbortController();

  (async () => {
    try {
      const data = await getAuditLogs({ signal: controller.signal });
      if (!alive) return;
      setApiLogs(Array.isArray(data?.logs) ? data.logs : []);
    } catch (err) {
      if (!alive) return;
      if (err?.name === 'CanceledError') return;
      setPageError(err?.response?.data?.message || err?.message || 'Failed to fetch audit logs');
    } finally {
      if (alive) setLoading(false);
    }
  })();

  return () => {
    alive = false;
    controller.abort();
  };
}, []);

  // Filter sources (unique dropdowns)
  const uniqueUsers = useMemo(
    () => ['all', ...Array.from(new Set(apiLogs.map((l) => l.username).filter(Boolean))).sort()],
    [apiLogs]
  );
  const uniqueActions = useMemo(
    () => ['all', ...Array.from(new Set(apiLogs.map((l) => l.action))).sort()],
    [apiLogs]
  );
  const uniqueOrgs = useMemo(
    () => ['all', ...Array.from(new Set(apiLogs.map((l) => l.org_name).filter(Boolean))).sort()],
    [apiLogs]
  );
  const uniqueEmails = useMemo(
    () => ['all', ...Array.from(new Set(apiLogs.map((l) => l.email).filter(Boolean))).sort()],
    [apiLogs]
  );
  const uniqueResourceTypes = useMemo(
    () => ['all', ...Array.from(new Set(apiLogs.map((l) => l.resource_type).filter(Boolean))).sort()],
    [apiLogs]
  );

  // Derived rows
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inRange = (ts) =>
      (!from || dayjs(ts).isAfter(from.startOf('day'))) &&
      (!to || dayjs(ts).isBefore(to.endOf('day')));
    const match = (v) => !q || String(v || '').toLowerCase().includes(q);

    let rows = apiLogs
      .filter(
        (l) =>
          inRange(l.created_at) &&
          (userFilter === 'all' || l.username === userFilter) &&
          (actionFilter === 'all' || l.action === actionFilter) &&
          (orgFilter === 'all' || l.org_name === orgFilter) &&
          (emailFilter === 'all' || l.email === emailFilter) &&
          (resourceTypeFilter === 'all' || l.resource_type === resourceTypeFilter) &&
          (match(l.user_id) ||
            match(l.username) ||
            match(l.email) ||
            match(l.action) ||
            match(l.resource_type) ||
            match(l.resource_name) ||
            match(l.org_id) ||
            match(l.org_name) ||
            match(l.error_message) ||
            match(l.details?.login_method) ||
            match(l.details?.connector_name) ||
            match(l.details?.workflow_id))
      )
      .map((l, idx) => ({
        id: l.audit_id || idx,
        audit_id: l.audit_id,
        ts: l.created_at,
        username: l.username || l.user_id || '-',
        email: l.email || '-',
        user_id: l.user_id,
        action: l.action,
        resource_type: l.resource_type,
        resource_id: l.resource_id,
        resource_name: l.resource_name,
        org_name: l.org_name || l.org_id || '-',
        org_id: l.org_id,
        success: l.success,
        error_message: l.error_message,
        login_method: l.details?.login_method || null,
        details: l.details || {},
      }));

    if (sort.direction !== 'none' && sort.column) {
      rows = [...rows].sort((a, b) => {
        let A = a[sort.column],
          B = b[sort.column];
        if (sort.column === 'ts') {
          A = new Date(A).getTime();
          B = new Date(B).getTime();
          return sort.direction === 'asc' ? A - B : B - A;
        }
        if (sort.column === 'success') {
          A = A === true ? 1 : A === false ? 0 : -1;
          B = B === true ? 1 : B === false ? 0 : -1;
          return sort.direction === 'asc' ? A - B : B - A;
        }
        return sort.direction === 'asc'
          ? String(A ?? '').localeCompare(String(B ?? ''))
          : String(B ?? '').localeCompare(String(A ?? ''));
      });
    }
    return rows;
  }, [apiLogs, search, userFilter, actionFilter, orgFilter, emailFilter, resourceTypeFilter, from, to, sort]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalRuns = filtered.length;
    const successfulRuns = filtered.filter((r) => r.success === true).length;
    const failedRuns = filtered.filter((r) => r.success === false).length;
    const successRate = totalRuns > 0 ? Math.round((successfulRuns / totalRuns) * 100) : 0;
    
    // Calculate average duration (if available in data)
    // For now, using a placeholder calculation
    const avgDuration = '3.2m'; // This would need to be calculated from actual duration data if available
    
    return {
      totalRuns,
      successRate,
      failedRuns,
      avgDuration,
    };
  }, [filtered]);

  const displayed = showAll ? filtered : filtered.slice(0, 12);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const exportCsv = () => {
    const header = ['Timestamp', 'Username', 'Email', 'Action', 'Resource Type', 'Resource Name', 'Organization Name', 'Login Method'];
    const rows = filtered.map((r) => [
      r.ts,
      r.username ?? '',
      r.email ?? '',
      r.action,
      r.resource_type,
      r.resource_name ?? '',
      r.org_name ?? '',
      r.login_method ?? '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-log.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Prepare filter options
  const userOptions = useMemo(
    () => uniqueUsers.map((u) => ({ value: u, label: u === 'all' ? 'All' : u })),
    [uniqueUsers]
  );
  const actionOptions = useMemo(
    () => uniqueActions.map((a) => ({ value: a, label: a === 'all' ? 'All' : a })),
    [uniqueActions]
  );
  const orgOptions = useMemo(
    () => uniqueOrgs.map((org) => ({ value: org, label: org === 'all' ? 'All' : org })),
    [uniqueOrgs]
  );
  const emailOptions = useMemo(
    () => uniqueEmails.map((email) => ({ value: email, label: email === 'all' ? 'All' : email })),
    [uniqueEmails]
  );
  const resourceTypeOptions = useMemo(
    () => uniqueResourceTypes.map((rt) => ({ value: rt, label: rt === 'all' ? 'All' : rt })),
    [uniqueResourceTypes]
  );

  return (
    <>
      <Box sx={{ display: 'flex', width: '100%', bgcolor: '#FAFBFD', minHeight: '100vh' }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Admin"
          showConnectorIcon={false}
          showSearchIcon={true}
          searchIconClick={() => setSearchOpen(!searchOpen)}
          searchOpen={searchOpen}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search logs…"
          rightContent={
            <PrimaryButton
              data-tour="audit-export"
              variant="contained"
              onClick={exportCsv}
              startIcon={<DownloadRounded />}
              sx={{
                bgcolor: headerBgColor,
                textTransform: 'capitalize',
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 1.75, md: 2 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                '&:hover': {
                  bgcolor: headerBgColor,
                  opacity: 0.9,
                },
              }}
            >
              Export Logs
            </PrimaryButton>
          }
        />

        <Box sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{ mb: 2, border: '1px solid #fecaca', background: '#fee2e2', color: '#7f1d1d' }}
            >
              {pageError}
            </Alert>
          </Collapse>

          <AdminTabs
            items={[
            ...(isSuperAdmin
              ? [
                  {
                    to: '/admin/organizations',
                    label: 'Organizations',
                    icon: Business,
                  },
                ]
              : []),
            { to: '/admin/user', label: 'User Management', icon: Person4 },
            { to: '/admin/annoucement', label: 'Annoucement', icon: Announcement },
            { to: '/admin/setting', label: 'Application Settings', icon: SettingsRounded },
            { to: '/admin/audit-log', label: 'Audit Logs', icon: TrackChanges },
          ]}
        />

          <Box sx={{ mb: 1.5, mt: 1.5 }}>
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                fontWeight: 500,
                color: '#4A5565',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Monitor all system activities and runs
            </Typography>
          </Box>

          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            {/* Summary Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Total Runs"
                  value={summaryMetrics.totalRuns}
                  icon={Timeline}
                  iconColor="#155DFC"
                  iconBgColor="#DBEAFE"
                  variants={itemVariants}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Success Rate"
                  value={`${summaryMetrics.successRate}%`}
                  icon={CheckCircle}
                  iconColor="#059669"
                  iconBgColor="#DCFCE7"
                  variants={itemVariants}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Failed Runs"
                  value={summaryMetrics.failedRuns}
                  icon={Warning}
                  iconColor="#DC2626"
                  iconBgColor="#FEE2E2"
                  variants={itemVariants}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Avg Duration"
                  value={summaryMetrics.avgDuration}
                  icon={AccessTime}
                  iconColor="#9F22FC"
                  iconBgColor="#F3E8FF"
                  variants={itemVariants}
                />
              </Grid>
            </Grid>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: '4px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                mb: 3,
                bgcolor: '#F1F5FF',
              }}
            >
              <Box data-tour="audit-filters" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    alignItems: 'center',
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From"
                      value={from}
                      onChange={setFrom}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            minWidth: { xs: '100%', sm: 140 },
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                              border: '1px solid #E5E7EB',
                              background: '#FFFFFF',
                              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease',
                              '& fieldset': {
                                border: 'none',
                              },
                              '&:hover': {
                                borderColor: '#D1D5DB',
                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                              },
                              '&.Mui-focused': {
                                borderColor: '#1B2640',
                                boxShadow: '0 0 0 3px rgba(27, 38, 64, 0.1)',
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 0.75,
                              px: 1.25,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.875rem',
                              color: '#111827',
                            },
                            '& .MuiInputLabel-root': {
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.875rem',
                              color: '#6B7280',
                              fontWeight: 500,
                              '&.Mui-focused': {
                                color: '#1B2640',
                              },
                            },
                          },
                        },
                      }}
                    />
                    <DatePicker
                      label="To"
                      value={to}
                      onChange={setTo}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            minWidth: { xs: '100%', sm: 140 },
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                              border: '1px solid #E5E7EB',
                              background: '#FFFFFF',
                              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease',
                              '& fieldset': {
                                border: 'none',
                              },
                              '&:hover': {
                                borderColor: '#D1D5DB',
                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                              },
                              '&.Mui-focused': {
                                borderColor: '#1B2640',
                                boxShadow: '0 0 0 3px rgba(27, 38, 64, 0.1)',
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 0.75,
                              px: 1.25,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.875rem',
                              color: '#111827',
                            },
                            '& .MuiInputLabel-root': {
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.875rem',
                              color: '#6B7280',
                              fontWeight: 500,
                              '&.Mui-focused': {
                                color: '#1B2640',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <FilterSelect
                    type="select"
                    label="User"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    options={userOptions}
                    allOption
                    allOptionLabel="All"
                    allOptionValue="all"
                    sx={{ minWidth: { xs: '100%', sm: 140 } }}
                  />

                  <FilterSelect
                    type="select"
                    label="Action"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    options={actionOptions}
                    allOption
                    allOptionLabel="All"
                    allOptionValue="all"
                    sx={{ minWidth: { xs: '100%', sm: 140 } }}
                  />

                  {isSuperAdmin && (
                    <FilterSelect
                      type="select"
                      label="Organization"
                      value={orgFilter}
                      onChange={(e) => setOrgFilter(e.target.value)}
                      options={orgOptions}
                      allOption
                      allOptionLabel="All"
                      allOptionValue="all"
                      sx={{ minWidth: { xs: '100%', sm: 140 } }}
                    />
                  )}

                  <FilterSelect
                    type="select"
                    label="Email"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    options={emailOptions}
                    allOption
                    allOptionLabel="All"
                    allOptionValue="all"
                    sx={{ minWidth: { xs: '100%', sm: 140 } }}
                  />

                  <FilterSelect
                    type="select"
                    label="Resource Type"
                    value={resourceTypeFilter}
                    onChange={(e) => setResourceTypeFilter(e.target.value)}
                    options={resourceTypeOptions}
                    allOption
                    allOptionLabel="All"
                    allOptionValue="all"
                    sx={{ minWidth: { xs: '100%', sm: 140 } }}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: '4px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
              }}
            >
              <TableContainer data-tour="admin-table" sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{
                      width: '100%',
                      '& .MuiTableCell-root': {
                        py: 0.5,
                        px: 2,
                        borderBottom: '1px solid #E5E7EB',
                      },
                      '& thead .MuiTableCell-root': { py: 0.75, px: 1.5 },
                      '& .MuiTableRow-root': { height: 44 },
                    }}>
                  <TableHead sx={{ backgroundColor: headerBgColor }}>
                    <TableRow sx={{ 
                      '&:hover': { 
                        backgroundColor: alpha(headerBgColor, 0.92),
                      }, 
                      transition: 'background-color 0.2s ease' 
                    }}>
                    {[
                      { label: 'Sr No', key: 'sr' },
                      { label: 'Timestamp', key: 'ts' },
                      { label: 'Username', key: 'username' },
                      { label: 'Email', key: 'email' },
                      { label: 'Organization Name', key: 'org_name' },
                      { label: 'Login Method', key: 'login_method' },
                      { label: 'Action', key: 'action' },
                      { label: 'Resource Type', key: 'resource_type' },
                      { label: 'Resource Name', key: 'resource_name' },
                      // { label: 'Success', key: 'success' },
                      // { label: 'Error Message', key: 'error_message' },
                      
                    ].map((h) => (
                      <TableCell key={h.key} onClick={() => handleSort(h.key)} sx={thSxClickable}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {h.label}
                          {sort.column === h.key && sort.direction === 'asc' && (
                            <ArrowUpwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />
                          )}
                          {sort.column === h.key && sort.direction === 'desc' && (
                            <ArrowDownwardIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />
                          )}
                          {(!sort.column || sort.column !== h.key) && (
                            <UnfoldMoreIcon sx={{ fontSize: '0.875rem', ml: 0.5, opacity: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>Loading logs…</Typography>
                      </TableCell>
                    </TableRow>
                  ) : displayed.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                          <Typography sx={{ color: '#64748b', fontSize: '0.95rem', mb: 1 }}>No logs found.</Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>Adjust filters or date range.</Typography>
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <AnimatePresence>
                      {displayed.map((r, i) => {
                        const isExpanded = expandedRows.has(r.id);
                        return (
                          <React.Fragment key={r.id}>
                            <TableRow
                              component={motion.tr}
                              custom={i}
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                              sx={{
                                borderBottom: '1px solid #F3F4F6',
                                backgroundColor:
                                  i % 2 === 1 ? '#F3F5F7' : '#ffffff',
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 149, 98, 0.08)',
                                },
                              }}
                            >
                              <TableCell sx={tdSx}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRow(r.id)}
                                    sx={{
                                      padding: '2px',
                                      color: themeColor,
                                      '&:hover': {
                                        backgroundColor: 'rgba(59, 149, 98, 0.08)',
                                      },
                                    }}
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                    ) : (
                                      <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                    )}
                                  </IconButton>
                                  {i + 1}
                                </Box>
                              </TableCell>
                              <TableCell sx={tdSx}>{formatDate(r.ts, { includeTime: true })}</TableCell>
                              <TableCell sx={tdSx}>{r.username}</TableCell>
                              <TableCell sx={tdSx}>{r.email}</TableCell>
                              <TableCell sx={tdSx}>{r.org_name}</TableCell>
                              <TableCell sx={tdSx}>{r.login_method ?? '-'}</TableCell>
                              <TableCell sx={{ ...tdSx, textTransform: 'lowerCase' }}>{r.action}</TableCell>
                              <TableCell sx={tdSx}>{r.resource_type}</TableCell>
                              <TableCell sx={tdSx}>{r.resource_name ?? '-'}</TableCell>
                            </TableRow>
                            
                            <TableRow sx={{ display: isExpanded ? 'table-row' : 'none' }}>
                              <TableCell
                                sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }}
                                colSpan={9}
                              >
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box
                                    sx={{
                                      margin: 2,
                                      p: 2,
                                      backgroundColor: '#FAFBFC',
                                      borderRadius: '8px',
                                      border: '1px solid #E5E7EB',
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      component="div"
                                      sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: '#111827',
                                        mb: 1.5,
                                      }}
                                    >
                                      Additional Details
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: 2,
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6B7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                          }}
                                        >
                                          Audit ID
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {r.audit_id || '-'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6B7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                          }}
                                        >
                                          User ID
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {r.user_id || '-'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6B7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                          }}
                                        >
                                          Organization ID
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {r.org_id || '-'}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#6B7280',
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                          }}
                                        >
                                          Resource ID
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                                          {r.resource_id || '-'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Paper>

            {/* View All Button */}
            {filtered.length > 12 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1,
                    borderColor: themeColor,
                    color: themeColor,
                    '&:hover': {
                      borderColor: themeColor,
                      bgcolor: alpha(themeColor, 0.08),
                    },
                  }}
                >
                  {showAll ? 'View Less' : `View All (${filtered.length})`}
                </PrimaryButton>
              </Box>
            )}
          </motion.div>
        </Box>
      </Box>
    </Box>
      <OnboardingTour />
    </>
  );
}
